import React, { useEffect } from "react";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import DharmaWheel from "../components/DharmaWheel";
import { BABASAHEB_IMG } from "../components/BabasahebPortrait";

const REFORMERS = [
  {
    name: "Samrat Ashoka",
    badge: "The Kind Emperor",
    when: "304 BCE — 232 BCE",
    img: "/images/p79lwkvj-f4c400bb-78f4-417c-93ad-866e29fe3e97.png",
    story:
      "Ashoka was once a fierce emperor who won many battles. But after one big war he saw how much sadness it caused, and his heart changed forever. He learned about the Buddha's gentle teachings and chose kindness. He built hospitals (even for animals!), planted trees along roads, and sent teachers all over Asia to share peace.",
    fact: "The big blue wheel on the Indian flag, the Ashoka Chakra, is named after him!",
    activity: "Draw your own peace wheel with 24 spokes, what does each spoke mean to you?",
  },
  {
    name: "Chhatrapati Shivaji Maharaj",
    badge: "The Brave Ruler",
    when: "1630 — 1680",
    img: "/images/bvu9v8br-1567b038-0493-4561-b578-a0ab5b0d33e2.jpeg",
    story:
      "Shivaji was a brave king who built strong forts on hilltops and protected the common people. He believed every person, rich or poor, of any religion, deserves to live with dignity. He treated soldiers and farmers with respect and showed that a true leader serves their people.",
    fact: "He could ride a horse up a steep mountain fort, and his hill-forts are still standing today!",
    activity: "Build a fort from blankets, pillows or LEGO. Who would you protect inside?",
  },
  {
    name: "Mahatma Jyotirao Phule",
    badge: "The Teacher Who Opened Schools",
    when: "1827 — 1890",
    img: "/images/vqj1e8g6-804f88e5-cf8c-489e-a6b2-1bde052cec84.jpeg",
    story:
      "Long ago in India, girls were not allowed to go to school. Mahatma Phule and his amazing wife Savitribai said: that's not fair! Together they opened the very first school for girls in 1848. Some people threw stones at Savitribai for teaching, but she carried two saris and changed when she got to school. They never gave up.",
    fact: "Savitribai is called India's first woman teacher, and Phule called her his guru!",
    activity: "Say thank you to a teacher this week. They helped you learn just like Savitribai did.",
  },
  {
    name: "Periyar E.V. Ramasamy",
    badge: "The Question-Asker",
    when: "1879 — 1973",
    img: "/images/stg6ijqo-a859a2d4-fb40-4e44-8a5b-5f6278ccfba3.jpeg",
    story:
      "Periyar believed nobody should be treated as 'less than' anyone else. He kept asking, why? why? why?, about old rules that hurt people. He worked hard so girls and boys, and people of every group, would be treated with the same respect. Asking questions is how the world gets better!",
    fact: "Periyar lived to be 94 years old, and never stopped asking 'why?'",
    activity: "Today, ask three 'why?' questions about something you think is unfair.",
  },
  {
    name: "Babasaheb Dr. B.R. Ambedkar",
    badge: "The Champion of Equality",
    when: "1891 — 1956",
    img: BABASAHEB_IMG,
    story:
      "When Babasaheb was a little boy, he was made to sit on the floor at school because of his caste. But he loved learning so much that he travelled all the way to America and London to study! He became the very first person to write India's Constitution, the rulebook that says EVERY child is equal. Later he showed millions of people the path of the Buddha.",
    fact: "Babasaheb collected over 35,000 books, one of the biggest private libraries ever!",
    activity: "Read for 10 minutes today. Babasaheb said: 'Read, read, and read again.'",
  },
  {
    name: "Anna Bhau Sathe",
    badge: "The Storyteller",
    when: "1920 — 1969",
    img: "/images/mu0u5i90-3c3d1e73-15f0-4597-b6df-55efedd8e23b.jpeg",
    story:
      "Anna Bhau wrote songs and stories about the brave farmers, workers and ordinary people of his village. He went to school for only a year and a half, yet he wrote 35 whole books! He proved that a beautiful story can come from anyone, anywhere, if they have something true to say.",
    fact: "His songs (called 'powadas') were sung in town squares to bring people hope and courage.",
    activity: "Write a short story or song about someone in your family who works hard.",
  },
];

const SHELVES = [
  {
    label: "Ages 5–7 · Read with grown-ups",
    books: [
      ["The Three Questions", "Jon J. Muth", "A Buddhist parable about kindness."],
      ["Zen Shorts", "Jon J. Muth", "Three calm Zen tales for children."],
      ["Peaceful Piggy Meditation", "Kerry Lee MacLean", "Simple meditation for big feelings."],
      ["A Boy Named Bhim", "Indian publishers", "Young Babasaheb's childhood story."],
    ],
  },
  {
    label: "Ages 8–12 · Independent readers",
    books: [
      ["Buddha at Bedtime", "Dharmachari Nagaraja", "20 illustrated Jataka tales."],
      ["Buddha Stories", "Demi", "Picture-book biography of the Buddha."],
      ["Prince Siddhartha", "Jonathan Landaw & Janet Brooke", "Full life of the Buddha for older kids."],
      ["Bhimrao Ambedkar: The Man Who Wrote India's Constitution", "Children's Book Trust", "Babasaheb's life for middle-graders."],
      ["Shivaji the Great Maratha", "Amar Chitra Katha", "Graphic-novel biography of Shivaji."],
    ],
  },
  {
    label: "Ages 13+ · Teen readers",
    books: [
      ["Bhimayana: Experiences of Untouchability", "Srividya Natarajan & S. Anand", "Award-winning graphic novel."],
      ["Old Path White Clouds", "Thich Nhat Hanh", "The Buddha's life in 81 short chapters."],
      ["Annihilation of Caste (Annotated)", "Dr. B.R. Ambedkar", "Foundational essay, annotated edition."],
      ["The Essential Ambedkar", "Penguin", "Selected short essays by Babasaheb."],
    ],
  },
  {
    label: "For grown-ups & family reading",
    books: [
      ["The Buddha and His Dhamma", "Dr. B.R. Ambedkar", "Foundation text of Navayana Buddhism."],
      ["Ambedkar: A Life", "Shashi Tharoor", "Clear, recent biography of Babasaheb."],
      ["Caste Matters", "Suraj Yengde", "Modern essay on caste & dignity."],
      ["Why I Am a Buddhist", "Stephen T. Asma", "Gentle introduction to Buddhist philosophy."],
    ],
  },
];

const ACTIVITIES = [
  ["Draw a Dharma Wheel", "Draw your own peace wheel with 24 spokes. What does each spoke stand for in your family?"],
  ["A Letter of Thanks", "Write a short letter to a grandparent, teacher or neighbour. Babasaheb said gratitude is a form of strength."],
  ["Read for 10 Minutes", "Read a story together every evening, Buddha tales, Jataka tales, or any reformer's biography."],
  ["Quiet Breath", "Sit quietly for 1 minute before dinner. Notice your breath. That's how the Buddha taught children to begin meditation."],
  ["Ask 3 'Why?' Questions", "Like Periyar, pick something at school or in your community that doesn't seem fair, and ask why."],
  ["Help Someone Today", "Help a younger sibling, set the table, or share a snack. Small acts of kindness build a kinder world."],
];

export default function ReadingBooklet() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("print") === "1") {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <>
      {/* Screen-only top bar */}
      <div className="bg-navy text-pearl py-4 print:hidden" data-testid="booklet-bar">
        <div className="section-container flex items-center justify-between gap-4">
          <Link to="/children" className="inline-flex items-center gap-2 text-pearl/80 hover:text-pearl text-sm" data-testid="booklet-back">
            <ArrowLeft size={16} /> Back to Children's Corner
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="booklet-print-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-navy hover:bg-gold-light rounded-full text-sm font-medium"
          >
            <Printer size={16} /> Print or save as PDF
          </button>
        </div>
      </div>

      <div className="booklet bg-pearl text-navy">
        {/* COVER PAGE */}
        <article className="page page-cover">
          <div className="cover-mark">
            <DharmaWheel size={120} color="#CFA052" ringColor="#001B3A" />
          </div>
          <p className="eyebrow text-saffron">Ambedkarite Buddhist Global Network UK</p>
          <h1 className="cover-title">Family Reading Booklet</h1>
          <p className="cover-sub">
            Stories, reading lists and gentle activities for children and parents.
          </p>
          <div className="cover-quote">
            "Educate, Agitate, Organise."
            <span>— Babasaheb Dr. B.R. Ambedkar</span>
          </div>
          <div className="cover-foot">
            <div>
              <strong>For:</strong> _______________________________
            </div>
            <div className="mt-1">
              <strong>From:</strong> _______________________________
            </div>
          </div>
        </article>

        {/* CONTENTS */}
        <article className="page">
          <p className="eyebrow">In this booklet</p>
          <h2 className="page-title">Contents</h2>
          <ul className="contents-list">
            <li><span>1</span> Six big-hearted reformers</li>
            <li><span>2</span> Reading shelf, by age group</li>
            <li><span>3</span> Six gentle weekend activities</li>
            <li><span>4</span> A note for parents</li>
          </ul>
          <p className="contents-note">
            Take this booklet to your local public library, most titles are available
            at Birmingham, Leeds, Manchester and London libraries. Or order online.
          </p>
        </article>

        {/* REFORMER STORIES, one per page */}
        {REFORMERS.map((r, idx) => (
          <article key={r.name} className="page">
            <p className="eyebrow text-saffron">{r.badge}</p>
            <div className="story-head">
              <div className="story-photo">
                <img src={r.img} alt={r.name} />
              </div>
              <div>
                <h2 className="story-name">{r.name}</h2>
                <p className="story-when">{r.when}</p>
              </div>
            </div>
            <p className="story-body">{r.story}</p>
            <div className="story-callout fact">
              <strong>Did you know?</strong>
              <span>{r.fact}</span>
            </div>
            <div className="story-callout activity">
              <strong>Try this at home</strong>
              <span>{r.activity}</span>
            </div>
            <p className="page-num">{idx + 1} of 6 reformers</p>
          </article>
        ))}

        {/* READING SHELF */}
        <article className="page">
          <p className="eyebrow">The reading shelf</p>
          <h2 className="page-title">Books we love, by age group.</h2>
          {SHELVES.map((s) => (
            <div key={s.label} className="shelf">
              <h3 className="shelf-label">{s.label}</h3>
              <ol className="shelf-list">
                {s.books.map((b, i) => (
                  <li key={i}>
                    <strong>{b[0]}</strong>, <em>{b[1]}</em>
                    <br />
                    <span className="shelf-gist">{b[2]}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </article>

        {/* ACTIVITIES */}
        <article className="page">
          <p className="eyebrow">Family activities</p>
          <h2 className="page-title">Six gentle ideas for the weekend.</h2>
          <ol className="activities-list">
            {ACTIVITIES.map((a, i) => (
              <li key={i}>
                <strong>{a[0]}</strong>
                <span>{a[1]}</span>
              </li>
            ))}
          </ol>
        </article>

        {/* PARENT NOTE / BACK COVER */}
        <article className="page page-back">
          <p className="eyebrow text-saffron">A note for parents</p>
          <h2 className="page-title">Talk gently. Listen always. Read together.</h2>
          <p className="back-body">
            The reformers we honour at ABGN-UK lived through hardships we hope our children never face.
            When discussing topics like caste, courage and equality, use age-appropriate language and
            invite questions. The goal isn't to impress facts onto our children, it's to build an
            inner sense of dignity, kindness and curiosity that they will carry for life.
          </p>
          <blockquote className="back-quote">
            "A great man is the servant of society."
            <span>— Babasaheb Dr. B.R. Ambedkar</span>
          </blockquote>
          <div className="back-meta">
            <div><strong>Ambedkarite Buddhist Global Network UK, United Kingdom</strong></div>
            <div>info@ambedkaritebuddhistglobalnetworkuk.com</div>
            <div className="mt-3 italic text-xs text-slate-500">
              Inspired by Babasaheb · United in Dhamma · Empowering Generations
            </div>
          </div>
        </article>
      </div>

      <style>{`
        .booklet {
          max-width: 800px;
          margin: 0 auto;
          padding: 32px 16px;
        }
        .page {
          background: #fff;
          border: 1px solid rgba(0,27,58,0.12);
          border-radius: 6px;
          padding: 56px 56px;
          margin-bottom: 24px;
          min-height: 1050px;
          position: relative;
          page-break-after: always;
          break-after: page;
        }
        .page-cover {
          background: linear-gradient(180deg, #FDFBF7 0%, #F5EDDD 100%);
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .cover-mark { margin-bottom: 32px; }
        .cover-title {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 64px;
          line-height: 1.05;
          color: #001B3A;
          margin: 12px 0 16px;
        }
        .cover-sub {
          font-size: 18px;
          color: #4A5568;
          max-width: 500px;
          margin: 0 auto 36px;
        }
        .cover-quote {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-size: 22px;
          color: #CFA052;
          border-top: 1px solid rgba(207,160,82,0.4);
          border-bottom: 1px solid rgba(207,160,82,0.4);
          padding: 20px 0;
          width: 80%;
          margin: 24px auto;
        }
        .cover-quote span {
          display: block;
          font-style: normal;
          font-size: 12px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #6b7280;
          margin-top: 8px;
        }
        .cover-foot {
          margin-top: 48px;
          font-size: 14px;
          color: #4A5568;
          text-align: left;
          width: 80%;
          margin-left: auto;
          margin-right: auto;
        }
        .page-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 42px;
          line-height: 1.1;
          color: #001B3A;
          margin: 8px 0 32px;
        }
        .contents-list {
          list-style: none;
          padding: 0;
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          color: #001B3A;
        }
        .contents-list li {
          padding: 14px 0;
          border-bottom: 1px solid rgba(0,27,58,0.1);
          display: flex;
          gap: 24px;
          align-items: baseline;
        }
        .contents-list span {
          color: #CFA052;
          font-weight: 600;
          width: 36px;
        }
        .contents-note {
          margin-top: 40px;
          padding: 20px;
          background: rgba(224,122,95,0.08);
          border-left: 4px solid #E07A5F;
          font-size: 14px;
          color: #4A5568;
        }
        .story-head {
          display: flex;
          gap: 24px;
          align-items: flex-start;
          margin-top: 12px;
          margin-bottom: 24px;
        }
        .story-photo {
          width: 160px;
          height: 200px;
          flex-shrink: 0;
          overflow: hidden;
          border-radius: 4px;
          border: 1px solid rgba(0,27,58,0.1);
          background: #f5edd5;
        }
        .story-photo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .story-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          line-height: 1.05;
          color: #001B3A;
          margin: 0 0 6px;
        }
        .story-when {
          font-size: 13px;
          letter-spacing: 0.1em;
          color: #6b7280;
          margin: 0;
          font-style: italic;
        }
        .story-body {
          font-size: 16px;
          line-height: 1.7;
          color: #1f2937;
          margin: 0 0 24px;
        }
        .story-callout {
          padding: 16px 20px;
          border-radius: 8px;
          margin-bottom: 14px;
          font-size: 14px;
          line-height: 1.5;
        }
        .story-callout strong {
          display: block;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .story-callout.fact {
          background: rgba(224,122,95,0.1);
          border-left: 3px solid #E07A5F;
          color: #001B3A;
        }
        .story-callout.fact strong { color: #C44A2D; }
        .story-callout.activity {
          background: rgba(207,160,82,0.15);
          border-left: 3px solid #CFA052;
          color: #001B3A;
        }
        .story-callout.activity strong { color: #8B6E2A; }
        .page-num {
          position: absolute;
          bottom: 24px;
          right: 56px;
          font-size: 11px;
          letter-spacing: 0.18em;
          color: #9ca3af;
          text-transform: uppercase;
        }
        .shelf { margin-bottom: 28px; }
        .shelf-label {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          color: #CFA052;
          padding-bottom: 6px;
          border-bottom: 1px solid rgba(207,160,82,0.3);
          margin-bottom: 12px;
        }
        .shelf-list {
          padding-left: 20px;
          font-size: 14px;
          line-height: 1.6;
          color: #1f2937;
        }
        .shelf-list li { margin-bottom: 10px; }
        .shelf-gist { color: #6b7280; }
        .activities-list {
          padding-left: 22px;
          font-size: 16px;
          line-height: 1.6;
          counter-reset: act;
        }
        .activities-list li {
          margin-bottom: 18px;
          padding-left: 8px;
        }
        .activities-list strong {
          color: #001B3A;
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          font-weight: 600;
          display: block;
          margin-bottom: 4px;
        }
        .activities-list span {
          color: #4A5568;
          font-size: 15px;
        }
        .page-back { background: #001B3A; color: #FDFBF7; }
        .page-back .page-title { color: #FDFBF7; }
        .back-body {
          font-size: 16px;
          line-height: 1.7;
          color: rgba(253,251,247,0.85);
          margin-bottom: 32px;
        }
        .back-quote {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-size: 26px;
          color: #CFA052;
          padding-left: 20px;
          border-left: 3px solid #CFA052;
          margin: 24px 0 36px;
        }
        .back-quote span {
          display: block;
          font-style: normal;
          font-size: 12px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(253,251,247,0.6);
          margin-top: 8px;
        }
        .back-meta {
          margin-top: 48px;
          padding-top: 20px;
          border-top: 1px solid rgba(253,251,247,0.2);
          font-size: 13px;
          color: rgba(253,251,247,0.85);
        }

        @media print {
          @page {
            size: A4;
            margin: 14mm;
          }
          html, body { background: #fff !important; }
          .booklet {
            max-width: none;
            margin: 0;
            padding: 0;
          }
          .page {
            border: none;
            border-radius: 0;
            margin: 0;
            padding: 0 0 16mm 0;
            min-height: auto;
            box-shadow: none;
          }
          .page-cover, .page-back {
            padding: 30mm 10mm;
          }
          .page:last-child { page-break-after: auto; }
        }
      `}</style>
    </>
  );
}
